import DocumentTitle from "react-document-title";
import { Separator, Spinner, SpinnerSize } from "@fluentui/react";
import Auth from "@/Auth/Auth";
import { PaymentInfoSubscription } from "./PaymentInfoSubscription";
import { useHistory, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExceededResult } from "@/services/OrderService";
import { currencyFormatter } from "@/utils/numberFormatter";
import SavingsPlanService from "@/services/SavingsPlanService";
import { ITableSPlansActive } from "@/components/SavePlans/types/SavePlansTypes";
import { Contracts } from "../RePaymentSubscription";
import * as Style from "../RePaymentSubscriptionStyle";
import ElementService from "@/services/ElementsService";
import ElementsTable, { TElementsResponse } from "./ElementsTable";
import { ISubscription } from "@/services/SubscriptionService";
import UsersSubscription, { TSubscriptionOptionsDTO, TUpgradeOptionDTO } from "@/services/UsersSubscriptionService";
import { toSubscriptionFromOption } from "../subscriptionOptionsRules";
import { resolveElementEarthPrice } from "../elementsEarthRules";
import { GenericMessageModalWithoutButton } from "@/components/Modals/GenericModal";
import { processUpgradePayment } from "./ProcessUpgradeSusbription";
import
{
    buildRepaymentContracts,
    canPayUpgrade,
    eurosToCents,
    getFinalPriceCents,
    getUpgradeNotice,
    isKnownUpgradeMode,
    resolvePreselectedUpgradeOption,
    resolveUsersSubscriptionId,
} from "./upgradeSubscriptionRules";

const BASE_CLASS = "upgradeSubscription";

const InitialElementsTotals: TElementsResponse =
{
    totalWater: 0,
    totalIA: 0,
    totalEarth: 0,
    totalDiscountByElements: 0,
    isValid: true,
    lines: [],
};

const areElementLinesEqual = (left: TElementsResponse["lines"], right: TElementsResponse["lines"]): boolean =>
{
    if(left.length !== right.length)
    {
        return false;
    }

    return left.every((line, index) =>
    {
        const other = right[index];
        return line.elementAmountId === other.elementAmountId && line.water === other.water && line.ia === other.ia && line.earth === other.earth;
    });
};

const UpgradeSubscription = () =>
{
    const activePlan = Auth.getActivePlan();
    const user = Auth.getUserProfile();
    const userId: number | undefined = user?.id;
    const history = useHistory();
    const location = useLocation<{ limitExceeded?: ExceededResult }>();

    // Detalle del límite superado que envía la pantalla que originó el 409. Se lee una sola vez y se
    // guarda en estado para que el modal no reaparezca al recargar (ver efecto de history.replace).
    const [limitExceeded] = useState<ExceededResult | undefined>(() => location.state?.limitExceeded);
    const [showModalNavigate, setShowModalNavigate] = useState<boolean>(!!limitExceeded);

    // Suscripción a mejorar: la del 409 o, si se entra por el enlace directo, la del plan activo.
    const [usersSubscriptionId] = useState<number | undefined>(() =>
        resolveUsersSubscriptionId(location.state?.limitExceeded?.usersSubscriptionId, activePlan?.userSubscriptionId));

    const [allSavingsPlans, setAllSavingsPlans] = useState<ITableSPlansActive[]>();
    const [elementWaterPrice, setElementWaterPrice] = useState<number>(0);
    // Respuesta de GET subscriptionOptions: fechas, tarifa actual, opciones de mejora y de renovación.
    const [subscriptionOptions, setSubscriptionOptions] = useState<TSubscriptionOptionsDTO>();
    const [selectedOption, setSelectedOption] = useState<TUpgradeOptionDTO | null>(null);
    const [elementsTotals, setElementsTotals] = useState<TElementsResponse>(InitialElementsTotals);
    const [isLoading, setIsLoading] = useState<boolean>(usersSubscriptionId !== undefined);
    const [loadError, setLoadError] = useState<string | undefined>(usersSubscriptionId === undefined
        ? "No se ha encontrado ninguna suscripción activa que mejorar. Vuelve a Mis Planes y selecciona el plan cuya suscripción quieres mejorar."
        : undefined);
    const [paymentError, setPaymentError] = useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // F6: se elimina el state de la navegación para que un F5 no vuelva a mostrar el modal del 409.
    useEffect(() =>
    {
        if(location.state?.limitExceeded !== undefined)
        {
            history.replace(location.pathname);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        if(!userId)
        {
            history.push("/savingsPlans");
            return;
        }

        SavingsPlanService.getSavingsPlansAndStatus(userId).then((plans: ITableSPlansActive[]) =>
        {
            setAllSavingsPlans(plans ?? []);
        })
        .catch((error: any) =>
        {
            console.error("UpgradeSubscription: error en getSavingsPlansAndStatus", error);
            setAllSavingsPlans([]);
        });
    }, [userId, history]);

    useEffect(() =>
    {
        ElementService.getLastElementsPrice().then((waterPrice: number) =>
        {
            if(typeof waterPrice !== "number" || isNaN(waterPrice))
            {
                return;
            }
            setElementWaterPrice(waterPrice);
        })
        .catch((error: any) => console.error("UpgradeSubscription: error en getLastElementsPrice", error));
    }, []);

    // Opciones de mejora y renovación de la suscripción (el backend es la única fuente de las reglas de tarifa).
    useEffect(() =>
    {
        if(usersSubscriptionId === undefined)
        {
            return;
        }

        let cancelled = false;

        UsersSubscription.getSubscriptionOptions(usersSubscriptionId).then((response: TSubscriptionOptionsDTO) =>
        {
            if(cancelled)
            {
                return;
            }

            if(!response || !response.currentSubscription || !response.upgrade)
            {
                setLoadError("No se ha podido obtener la información de tu suscripción. Por favor, inténtelo de nuevo más tarde.");
                return;
            }

            // Un modo que el front no conoce no se trata como prorrateado: error y no se deja pagar.
            if(response.upgrade.allowed === true && !isKnownUpgradeMode(response.upgrade.mode))
            {
                console.error("UpgradeSubscription: modo de mejora desconocido", response.upgrade.mode);
                setLoadError("No se ha podido determinar el tipo de mejora de tu suscripción. Por favor, inténtelo de nuevo más tarde o contacta con soporte.");
                return;
            }

            setSubscriptionOptions(response);
            setSelectedOption(resolvePreselectedUpgradeOption(response.upgrade.options, response.upgrade.recommendedId));
        })
        .catch((error: any) =>
        {
            console.error("UpgradeSubscription: error en getSubscriptionOptions", error);
            if(cancelled)
            {
                return;
            }

            const status: number | undefined = error?.response?.status;
            setLoadError(status === 401 || status === 403 || status === 404
                ? "No se ha encontrado la suscripción que quieres mejorar o no pertenece a tu usuario."
                : "No se ha podido obtener la información de tu suscripción. Por favor, inténtelo de nuevo más tarde.");
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setIsLoading(false);
            }
        });

        return () =>
        {
            cancelled = true;
        };
    }, [usersSubscriptionId]);

    // Contratos con Elements de la suscripción que se mejora (como en la renovación).
    const savingPlansElements = useMemo((): Contracts[] =>
    {
        if(!allSavingsPlans?.length || usersSubscriptionId === undefined)
        {
            return [];
        }

        return allSavingsPlans
            .filter((plan: ITableSPlansActive) => plan.status === 4 &&
                plan.elementAmountId !== null && plan.elementAmountId !== undefined &&
                plan.userSubscriptionId === usersSubscriptionId)
            .sort((a, b) =>
            {
                return a.startDate
                    .split("-")
                    .reverse()
                    .join("-")
                    .localeCompare(b.startDate.split("-").reverse().join("-"));
            })
            .map((plan: ITableSPlansActive) =>
            ({
                contract: plan.finametrixContract,
                elementAmountId: plan.elementAmountId,
                elementsIA: plan.elementsIA,
                elementsWater: plan.elementsWater,
                elementsEarth: plan.elementsEarth,
                savingPlanName: plan.planName,
                hasElements: plan.hasElements,
                isSubscriptionRenewalBeforeExpiration: plan.isSubscriptionRenewalBeforeExpiration,
                savingPlanId: plan.id,
            }));
    }, [allSavingsPlans, usersSubscriptionId]);

    const handleTotalsChange = useCallback((totals: TElementsResponse) =>
    {
        setElementsTotals((prev) =>
        {
            if(prev.totalWater === totals.totalWater && prev.totalIA === totals.totalIA && prev.totalEarth === totals.totalEarth &&
               prev.totalDiscountByElements === totals.totalDiscountByElements && prev.isValid === totals.isValid &&
               areElementLinesEqual(prev.lines, totals.lines))
            {
                return prev;
            }

            return totals;
        });
    }, []);

    const upgrade = subscriptionOptions?.upgrade;
    const renewal = subscriptionOptions?.renewal;
    const currentSubscriptionId = subscriptionOptions?.currentSubscription?.id;
    // Precio de los Elements Tierra con el que cobra el backend; 0 si no lo envía (quedan deshabilitados).
    const elementEarthPrice = resolveElementEarthPrice(subscriptionOptions?.elementEarthPrice);
    const upgradeAllowed = upgrade?.allowed === true && isKnownUpgradeMode(upgrade?.mode);
    const upgradeOptions = useMemo((): TUpgradeOptionDTO[] => upgrade?.options ?? [], [upgrade]);
    const subscriptionsAvailable = useMemo((): ISubscription[] => upgradeOptions.map((option) => toSubscriptionFromOption(option)), [upgradeOptions]);
    const selectedSubscription = useMemo((): ISubscription | null => selectedOption ? toSubscriptionFromOption(selectedOption) : null, [selectedOption]);
    const recommendedOption = upgradeOptions.find((option) => option.id === upgrade?.recommendedId) ?? null;
    // Precio a cobrar antes de Elements: chargePrice del backend (el front ya no calcula el prorrateo).
    const chargePrice = selectedOption?.chargePrice ?? 0;
    const chargeCents = eurosToCents(chargePrice);
    const finalPriceCents = getFinalPriceCents(chargeCents, elementsTotals.totalDiscountByElements);
    const noticeText = getUpgradeNotice(upgrade?.mode, subscriptionOptions?.endDate, upgrade?.newEndDate, selectedOption?.name);
    const canPay = loadError === undefined && currentSubscriptionId !== undefined && canPayUpgrade(
    {
        selectedSubscriptionId: selectedOption?.id,
        usersSubscriptionId,
        elementsValid: elementsTotals.isValid,
        upgradeAllowed,
        isSubmitting,
    });

    const handleSelectedSubscriptionChange = (subscription: ISubscription) =>
    {
        setPaymentError(undefined);
        setSelectedOption(upgradeOptions.find((option) => option.id === subscription.id) ?? null);
    };

    const handlePayment = async () =>
    {
        if(!canPay || !userId || usersSubscriptionId === undefined || !selectedOption || currentSubscriptionId === undefined)
        {
            return;
        }

        setPaymentError(undefined);
        setIsSubmitting(true);

        try
        {
            const result = await processUpgradePayment(
            {
                amountCents: finalPriceCents,
                reference: `${userId}-${Date.now()}`,
                userId,
                currentSubscriptionId: currentSubscriptionId,
                subscriptionId: selectedOption.id,
                usersSubscriptionId,
                elementLines: elementsTotals.lines,
            });

            if(result.kind === "redirect")
            {
                // Se mantiene el botón bloqueado mientras el navegador sale hacia Inespay o la URL de éxito.
                window.location.href = result.url;
                return;
            }

            setPaymentError(result.message);
        }
        catch(error: any)
        {
            console.error("UpgradeSubscription: error inesperado en el pago", error);
            setPaymentError("Se ha producido un error al procesar su transacción. Por favor, inténtelo de nuevo más tarde.");
        }

        setIsSubmitting(false);
    };

    // Mejora no permitida: se ofrece la renovación con la tarifa que exige el backend (o la actual si no exige ninguna).
    const goToRenewal = () =>
    {
        const contracts = usersSubscriptionId !== undefined ? buildRepaymentContracts(allSavingsPlans, usersSubscriptionId) : null;
        if(contracts === null)
        {
            // Sin datos de contratos RePaymentSubscription no puede funcionar: se va a Mis Planes, desde donde se renueva.
            history.push("/savingsPlans");
            return;
        }

        history.push("/Repayment",
        {
            contracts: contracts,
            newSubscriptionId: renewal?.requiredSubscriptionId ?? undefined,
        });
    };

    // Texto informativo con el desglose del límite superado.
    const limitExceededBody = limitExceeded &&
    (
        <>
            <span>
                ¡Enhorabuena! Has alcanzado el límite de tu suscripción actual, por lo que
                no se ha podido completar tu operación. Si deseas poder continuar con tus
                operaciones deberás mejorar tu suscripción.{" "}
                {upgradeAllowed && recommendedOption
                    ? <>Te recomendamos la suscripción {recommendedOption.name}, o puedes seleccionar otra suscripción en el panel de mejora.{" "}</>
                    : null}
            </span>
            {limitExceeded.limit != null &&
            (
                <span style={{display: "block"}}>
                    Límite de tu suscripción:{" "}
                    <b>{currencyFormatter.format(limitExceeded.limit)}</b>
                </span>
            )}
            <span style={{display: "block"}}>
                Valor actual de tu cartera:{" "}
                <b>{currencyFormatter.format(limitExceeded.currentAmount)}</b>
            </span>
            <span style={{display: "block", marginTop: "0.5em"}}>
                Mejora tu suscripción para poder continuar con la operación.
            </span>
        </>
    );

    return(
        <DocumentTitle title="IronIA - Mejora de Suscripción">
            <div className={BASE_CLASS}>
                <div className={`${BASE_CLASS}__header`}>
                    <h2>Mejora de Suscripción</h2>
                    <p>
                        Este método de pago conecta con tu banca online utilizando las
                        claves de acceso de tu banca online.
                    </p>
                    <Separator />
                </div>
                {isLoading &&
                (
                    <>
                        <Spinner size={SpinnerSize.large} style={{paddingTop: "2em"}} />
                        <Style.TextLoading>
                            Estamos cargando los datos de tu suscripción...
                        </Style.TextLoading>
                    </>
                )}
                {!isLoading && loadError !== undefined &&
                (
                    <Style.Card style={{marginTop: "2em"}}>
                        <Style.CardTitle style={{color: "#CC214F"}}>
                            Error al cargar la información
                        </Style.CardTitle>
                        <div style={{padding: "2em", textAlign: "center"}}>
                            <p>{loadError}</p>
                        </div>
                    </Style.Card>
                )}
                {!isLoading && loadError === undefined &&
                (
                    <>
                        {!upgradeAllowed &&
                        (
                            <Style.Card style={{marginTop: "2em"}}>
                                <Style.CardTitle style={{color: "#CC214F"}}>
                                    No es posible mejorar tu suscripción
                                </Style.CardTitle>
                                <div style={{padding: "2em", textAlign: "center"}}>
                                    <p style={{marginBottom: "1em"}}>
                                        {upgrade?.notAllowedReason || "Tu suscripción actual no se puede mejorar."}
                                    </p>
                                    {renewal?.allowed === true &&
                                    (
                                        <Style.PayButton style={{float: "none"}} onClick={goToRenewal}>
                                            Ir a la renovación
                                        </Style.PayButton>
                                    )}
                                </div>
                            </Style.Card>
                        )}
                        {upgradeAllowed && upgradeOptions.length === 0 &&
                        (
                            <Style.Card style={{marginTop: "2em"}}>
                                <div style={{padding: "2em", textAlign: "center"}}>
                                    <p>No hay ninguna suscripción superior a tu suscripción actual a la que puedas mejorar.</p>
                                </div>
                            </Style.Card>
                        )}
                        {upgradeAllowed &&
                        (
                            <ElementsTable
                                elementWaterPrice={elementWaterPrice}
                                elementEarthPrice={elementEarthPrice}
                                savingPlansElements={savingPlansElements}
                                newSubscriptionPrice={chargePrice}
                                onTotalsChange={handleTotalsChange}
                            />
                        )}
                        <PaymentInfoSubscription
                            subscriptionsAvailable={subscriptionsAvailable}
                            selectedSubscription={selectedSubscription}
                            onSelectedSubscriptionChange={handleSelectedSubscriptionChange}
                            elementsTotal={elementsTotals}
                            elementWaterPrice={elementWaterPrice}
                            elementEarthPrice={elementEarthPrice}
                            mode={upgrade?.mode ?? null}
                            remainingDays={subscriptionOptions?.remainingDays ?? 0}
                            chargePrice={chargePrice}
                            finalPrice={finalPriceCents / 100}
                            noticeText={upgradeAllowed ? noticeText : null}
                            canPay={canPay}
                            isSubmitting={isSubmitting}
                            errorMessage={paymentError}
                            onPay={handlePayment}
                        />
                    </>
                )}
                {limitExceeded &&
                (
                    <GenericMessageModalWithoutButton
                        body={limitExceededBody}
                        title="Límite de suscripción superado"
                        showModal={showModalNavigate}
                        onClose={() => setShowModalNavigate(false)}
                    />
                )}
            </div>
        </DocumentTitle>
    );
};

export default UpgradeSubscription;
